import { Component, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="max-w-lg mx-auto px-4 py-16">
          <div className="card text-center">
            <div className="flex justify-center mb-4">
              <AlertTriangle className="w-12 h-12 text-accent" />
            </div>
            <h1 className="text-2xl font-extrabold text-navy mb-2">
              Something went wrong
            </h1>
            <p className="text-gray-400 mb-6">
              {this.state.error?.message || 'An unexpected error occurred. Please try again.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" className="btn btn-primary" onClick={() => this.setState({ hasError: false, error: null })}>
                Go Home
              </Link>
              <Link to="/clinics" className="btn btn-outline" onClick={() => this.setState({ hasError: false, error: null })}>
                Find a Clinic
              </Link>
            </div>
            <div className="mt-8 pt-6 border-t border-gray-100 text-sm text-gray-400">
              <p>
                Medical emergency? Call{' '}
                <a href="tel:911" className="text-danger font-semibold hover:underline">911</a>
                {' '}or{' '}
                <a href="tel:919" className="text-danger font-semibold hover:underline">919</a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
